import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
  Avatar,
  TextField,
  Stack,
} from '@mui/material';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuOpenOutlinedIcon from '@mui/icons-material/MenuOpenOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useAppShell } from './AppShellContext';
import { NAV_ITEMS } from './routeConfig';
import { layout } from '../theme/tokens';
import { ConfirmDialog, Select } from '../components/ui';
import { apiPost } from '../utils/apiAuth';
import config from '../config';
import { ContentTypeConstants, normalizeEditorKey } from '../constants/contentCreationConstants';

const drawerWidthExpanded = layout.sidebarWidthExpanded;
const drawerWidthCollapsed = layout.sidebarWidthCollapsed;

const LOGO_SRC = `${process.env.PUBLIC_URL}/Exskilence logo.svg`;
const ICON_SRC = `${process.env.PUBLIC_URL}/Exskilence icon.svg`;

function isProjectCreationContext() {
  try {
    const navSource = sessionStorage.getItem('contentCreation_navSource');
    if (navSource === 'project') return true;
    if (navSource === 'content-bank' || navSource === 'testing') return false;
    // Fallback for older sessions that predate navSource
    return sessionStorage.getItem('contentCreation_freezeFilters') === 'true';
  } catch {
    return false;
  }
}

function useUserInfo() {
  const userName = sessionStorage.getItem('Name') || 'User';

  const initials = userName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return { userName, initials };
}

function getSearchTypeOptions() {
  const access = sessionStorage.getItem('access') || '';
  const allTypes = Object.values(ContentTypeConstants);
  const allowed = allTypes.filter((item) => access.includes(item.key));
  const source = allowed.length > 0 ? allowed : allTypes;
  return source.map((item) => ({ value: item.key, label: item.label }));
}

function getIdFieldLabel(contentType) {
  if (contentType === ContentTypeConstants.VIDEO.key) return 'Video ID';
  if (contentType === ContentTypeConstants.NOTES.key) return 'Notes ID';
  if (contentType === ContentTypeConstants.NOTEBOOK.key) return 'Notebook ID';
  return 'Question ID';
}

export default function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarExpanded, toggleSidebar } = useAppShell();
  const { userName, initials } = useUserInfo();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchType, setSearchType] = useState('');
  const [searchId, setSearchId] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [notFoundOpen, setNotFoundOpen] = useState(false);

  const searchTypeOptions = useMemo(() => getSearchTypeOptions(), [searchOpen]);
  const drawerWidth = sidebarExpanded ? drawerWidthExpanded : drawerWidthCollapsed;

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.removeItem('appShell_sidebarExpanded');
    navigate('/');
  };

  const closeSearch = () => {
    if (isSearching) return;
    setSearchOpen(false);
    setSearchId('');
    setSearchType('');
  };

  const handleSearch = async () => {
    const contentType = searchType.trim();
    const contentId = searchId.trim();
    if (!contentType || !contentId || isSearching) return;

    setIsSearching(true);
    try {
      const response = await apiPost(
        `${config.API_BASE_URL}/api/content_creation/lookup_content/`,
        {
          content_id: contentId,
          content_type: contentType,
        }
      );
      const result = response.data || {};
      const resolvedType = result.content_type || contentType;
      const editor =
        resolvedType === ContentTypeConstants.CODING.key
          ? normalizeEditorKey(result.editor)
          : '';

      sessionStorage.setItem('contentCreation_contentType', JSON.stringify(resolvedType));
      sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(result.topic_id || ''));
      sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(result.sub_topic_id || ''));
      sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(result.subject_id || ''));
      sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(result.subject_name || ''));
      sessionStorage.setItem('contentCreation_navSource', 'content-bank');
      sessionStorage.removeItem('contentCreation_freezeFilters');
      sessionStorage.removeItem('contentCreation_projectId');
      sessionStorage.removeItem('contentCreation_projectName');

      if (resolvedType === ContentTypeConstants.VIDEO.key) {
        sessionStorage.setItem('contentCreation_selectedVideo', JSON.stringify(contentId));
      } else if (resolvedType === ContentTypeConstants.NOTES.key) {
        sessionStorage.setItem('contentCreation_selectedNote', JSON.stringify(contentId));
      } else if (resolvedType === ContentTypeConstants.NOTEBOOK.key) {
        sessionStorage.setItem('contentCreation_selectedNotebook', JSON.stringify(contentId));
      } else {
        sessionStorage.setItem('contentCreation_selectedQuestion', JSON.stringify(contentId));
      }

      if (editor) {
        sessionStorage.setItem('contentCreation_selectedEditor', JSON.stringify(editor));
      } else {
        sessionStorage.removeItem('contentCreation_selectedEditor');
      }

      setSearchOpen(false);
      setSearchId('');
      setSearchType('');

      navigate('/content-creation', {
        state: {
          data: {
            subject_id: result.subject_id,
            subject_name: result.subject_name,
            topic_id: result.topic_id,
            sub_topic_id: result.sub_topic_id,
            content_type: resolvedType,
            contentId,
            editor: editor || undefined,
            freezeFilters: false,
            lookupNonce: Date.now(),
          },
        },
      });
    } catch (error) {
      setSearchOpen(false);
      setNotFoundOpen(true);
    } finally {
      setIsSearching(false);
    }
  };

  const isActive = (path) => {
    const { pathname } = location;

    if (path === '/dashboard') {
      return pathname === '/dashboard';
    }

    if (path === '/project-content') {
      if (pathname === '/project-content' || pathname === '/project-configuration') {
        return true;
      }
      if (pathname === '/content-creation') {
        return isProjectCreationContext();
      }
      if (pathname === '/coding-questions') {
        return sessionStorage.getItem('codingQuestions_returnPath') === '/project-content';
      }
      return false;
    }

    if (path === '/content-bank') {
      if (pathname === '/content-bank') return true;
      if (pathname === '/content-creation') {
        return !isProjectCreationContext();
      }
      if (pathname === '/coding-questions') {
        return sessionStorage.getItem('codingQuestions_returnPath') !== '/project-content';
      }
      return pathname.startsWith('/content-bank/');
    }

    return pathname === path || pathname.startsWith(`${path}/`);
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    const button = (
      <ListItemButton
        key={item.path}
        onClick={() => navigate(item.path)}
        selected={active}
        sx={{
          minHeight: 44,
          justifyContent: sidebarExpanded ? 'initial' : 'center',
          px: sidebarExpanded ? 2 : 1.5,
          mx: 1,
          mb: 0.5,
          borderRadius: 'var(--radius-md)',
          '&.Mui-selected': {
            backgroundColor: 'var(--color-sidebar-active-bg)',
            color: 'var(--color-sidebar-active)',
            '& .MuiListItemIcon-root': {
              color: 'var(--color-sidebar-active)',
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: sidebarExpanded ? 1.5 : 0,
            justifyContent: 'center',
            color: active ? 'var(--color-sidebar-active)' : 'var(--color-text-secondary)',
          }}
        >
          <Icon fontSize="small" />
        </ListItemIcon>
        {sidebarExpanded && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: active ? 600 : 500,
            }}
          />
        )}
      </ListItemButton>
    );

    if (!sidebarExpanded) {
      return (
        <Tooltip key={item.path} title={item.label} placement="right">
          <Box component="span" sx={{ display: 'block' }}>
            {button}
          </Box>
        </Tooltip>
      );
    }

    return button;
  };

  const searchButton = (
    <ListItemButton
      onClick={() => setSearchOpen(true)}
      selected={searchOpen}
      sx={{
        minHeight: 44,
        justifyContent: sidebarExpanded ? 'initial' : 'center',
        px: sidebarExpanded ? 2 : 1.5,
        mx: 1,
        mb: 0.5,
        borderRadius: 'var(--radius-md)',
        '&.Mui-selected': {
          backgroundColor: 'var(--color-sidebar-active-bg)',
          color: 'var(--color-sidebar-active)',
          '& .MuiListItemIcon-root': {
            color: 'var(--color-sidebar-active)',
          },
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: sidebarExpanded ? 1.5 : 0,
          justifyContent: 'center',
          color: searchOpen ? 'var(--color-sidebar-active)' : 'var(--color-text-secondary)',
        }}
      >
        <SearchOutlinedIcon fontSize="small" />
      </ListItemIcon>
      {sidebarExpanded && (
        <ListItemText
          primary="Search"
          primaryTypographyProps={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: searchOpen ? 600 : 500,
          }}
        />
      )}
    </ListItemButton>
  );

  const logoutButton = (
    <ListItemButton
      onClick={handleLogout}
      sx={{
        minHeight: 40,
        justifyContent: sidebarExpanded ? 'initial' : 'center',
        px: sidebarExpanded ? 1.5 : 1,
        mx: sidebarExpanded ? 1 : 0.5,
        borderRadius: 'var(--radius-md)',
        color: 'var(--color-error-main)',
        '&:hover': {
          backgroundColor: 'var(--color-error-surface)',
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: sidebarExpanded ? 1.5 : 0,
          justifyContent: 'center',
          color: 'inherit',
        }}
      >
        <LogoutOutlinedIcon fontSize="small" />
      </ListItemIcon>
      {sidebarExpanded && (
        <ListItemText
          primary="Logout"
          primaryTypographyProps={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}
        />
      )}
    </ListItemButton>
  );

  const userAvatar = (
    <Avatar
      sx={{
        width: sidebarExpanded ? 40 : 36,
        height: sidebarExpanded ? 40 : 36,
        bgcolor: 'var(--color-primary-main)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials}
    </Avatar>
  );

  const userSection = sidebarExpanded ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
      }}
    >
      {userAvatar}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{ fontSize: 'var(--font-size-sm)', lineHeight: 1.3 }}
        >
          {userName}
        </Typography>
      </Box>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
      <Tooltip title={userName} placement="right">
        <Box component="span">{userAvatar}</Box>
      </Tooltip>
    </Box>
  );

  return (
    <>
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid var(--color-sidebar-border)',
          backgroundColor: 'var(--color-sidebar-bg)',
          transition: 'width var(--transition-slow)',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'relative',
        },
      }}
    >
      <Box
        onClick={toggleSidebar}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarExpanded ? 'space-between' : 'center',
          px: sidebarExpanded ? 2 : 1,
          py: 1.5,
          minHeight: 56,
          flexShrink: 0,
          cursor: 'pointer',
          borderBottom: '1px solid var(--color-border-default)',
        }}
      >
        {sidebarExpanded ? (
          <>
            <Box
              component="img"
              src={LOGO_SRC}
              alt="Exskilence"
              sx={{
                height: 40,
                maxWidth: 'calc(100% - 28px)',
                objectFit: 'contain',
                objectPosition: 'left center',
                display: 'block',
              }}
            />
            <MenuOpenOutlinedIcon fontSize="small" sx={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
          </>
        ) : (
          <Box
            component="img"
            src={ICON_SRC}
            alt="Exskilence"
            sx={{
              height: 32,
              width: 32,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <List sx={{ pt: 1 }} disablePadding>
          {sidebarExpanded ? (
            searchButton
          ) : (
            <Tooltip title="Search" placement="right">
              <Box component="span" sx={{ display: 'block' }}>
                {searchButton}
              </Box>
            </Tooltip>
          )}
          {NAV_ITEMS.map((item) => renderNavItem(item))}
        </List>
      </Box>

      <Box sx={{ flexShrink: 0, borderTop: '1px solid var(--color-border-default)' }}>
        {userSection}
        <Divider />
        <Box sx={{ py: 0.5 }}>
          {sidebarExpanded ? (
            logoutButton
          ) : (
            <Tooltip title="Logout" placement="right">
              <Box component="span" sx={{ display: 'block' }}>
                {logoutButton}
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Drawer>
    <ConfirmDialog
      open={searchOpen}
      onClose={closeSearch}
      onConfirm={handleSearch}
      title="Search content"
      confirmLabel="Search"
      loading={isSearching}
      confirmDisabled={!searchType || !searchId.trim()}
      disableBackdropClose={isSearching}
      maxWidth="xs"
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Select
          label="Content type"
          value={searchType}
          onChange={(e) => setSearchType(e.target.value)}
          options={searchTypeOptions}
          placeholder="Select a type"
          displayEmpty
        />
        <TextField
          label={getIdFieldLabel(searchType)}
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSearch();
            }
          }}
          size="small"
          fullWidth
          autoFocus
        />
      </Stack>
    </ConfirmDialog>
    <ConfirmDialog
      open={notFoundOpen}
      onClose={() => setNotFoundOpen(false)}
      title="Question not exist"
      message="Question not exist"
      variant="alert"
      severity="error"
    />
    </>
  );
}
